import React, { useEffect, useState } from "react";
import Tree from "react-d3-tree";
import { db } from "../firebase";
import { collection, onSnapshot, doc, deleteDoc } from "firebase/firestore";
import FamilyForm from "./FamilyForm";

export default function FamilyTree() {
  const [familyData, setFamilyData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  // 🔄 Fetch live data from Firestore
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "FamilyTree"), (snapshot) => {
      const members = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      const buildTree = () => {
        const map = {};
        members.forEach((m) => {
          map[m.id] = {
            ...m,
            children: [],
            childrenIds: m.childrenIds || [],
            spouseId: m.spouseId || null,
            photoUrl: m.photoUrl || null,
          };
        });

        let root = null;
        members.forEach((m) => {
          if (m.parentId && map[m.parentId]) {
            map[m.parentId].children.push(map[m.id]);
          } else {
            root = map[m.id];
          }
        });

        return root;
      };

      setFamilyData(buildTree());
    });

    return () => unsubscribe();
  }, []);

  // 🗑 Delete a member
  const handleDelete = async (id) => {
    const member = findMemberById(familyData, id);

    if (!member) return;

    if (!member.parentId) {
      alert("Cannot delete the root member!");
      return;
    }

    if (window.confirm("Are you sure you want to delete this member?")) {
      await deleteDoc(doc(db, "FamilyTree", id));
    }
  };

  // 🔍 Helper to find member in tree
  const findMemberById = (node, id) => {
    if (!node) return null;
    if (node.id === id) return node;
    if (!node.children) return null;

    for (let child of node.children) {
      const found = findMemberById(child, id);
      if (found) return found;
    }
    return null;
  };

  // 🎨 Custom node UI (with photo support)
  const renderCustomNode = ({ nodeDatum }) => {
    const radius = 40;

    return (
      <g
        className="cursor-pointer hover:scale-110 transition-transform duration-200"
        onClick={() => setSelectedMember(nodeDatum)}
      >
        {/* Circle or Image */}
        {nodeDatum.photoUrl ? (
          <image
            href={nodeDatum.photoUrl}
            x={-radius}
            y={-radius}
            width={radius * 2}
            height={radius * 2}
            clipPath="circle(40px at center)"
          />
        ) : (
          <circle r={radius} fill="#2596be" stroke="#333" strokeWidth={2} />
        )}

     {/* Name */}
<g>
  {/* Background box with padding */}
  <rect
    x={-60}
    y={radius + 2}
    width={120}
    height={28}
    rx={6}
    ry={6}
    fill="#16a34a" // solid green
  />

{/* Name text */}
<text
  x={0}
  y={radius + 22}
  textAnchor="middle"
  style={{
    fontSize: "16px",
    fontWeight: "1000",
    fontFamily: "Arial, sans-serif",
    pointerEvents: "none",
    fill: "#FFFFFF",  // ✅ this is the correct way
  }}
>
  {nodeDatum.name}
</text>
</g>

{/* DOB */}
{nodeDatum.dob && (
  <text
    fill="#FFFFFF"
    x={0}
    y={radius + 50} // ⬅ pushed further down for spacing
    textAnchor="middle"
    style={{
      fontSize: "12px",
      fontStyle: "italic",
      background: "white",
    }}
  >
    {nodeDatum.dob}
  </text>
)}

        {/* Buttons */}
        <foreignObject x={radius + 10} y={-25} width={100} height={80}>
          <div className="flex flex-col gap-1">
            <button
              className="bg-yellow-400 px-2 py-1 rounded text-xs shadow hover:bg-yellow-500 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setEditId(nodeDatum.id);
                setShowForm(true);
              }}
            >
              ✏ Edit
            </button>
            <button
              className={`px-2 py-1 rounded text-xs shadow transition-colors ${
                !nodeDatum.parentId
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-red-500 text-white hover:bg-red-600"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                nodeDatum.parentId && handleDelete(nodeDatum.id);
              }}
              disabled={!nodeDatum.parentId}
            >
              🗑 Delete
            </button>
          </div>
        </foreignObject>
      </g>
    );
  };

  if (!familyData) return <p className="text-center mt-10">Loading family tree...</p>;

  return (
    <div style={{ width: "100%", height: "90vh", position: "relative" }}>
      {/* Bio Modal */}
{selectedMember && (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
    <div className="bg-white p-6 rounded shadow-lg max-w-xl w-full relative max-h-[80vh] overflow-y-auto">
      {/* Top Close Button */}
      <button
        className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
        onClick={() => setSelectedMember(null)}
      >
        ✖
      </button>

      {selectedMember.photoUrl && (
        <img
          src={selectedMember.photoUrl}
          alt={selectedMember.name}
          className="w-60 mx-auto mb-4 object-cover"
        />
      )}

      <h2 className="text-2xl font-bold mb-2 text-center">{selectedMember.name}</h2>

      {selectedMember.dob && (
        <p className="text-sm mb-2 text-center">
          DOB: {selectedMember.dob}
        </p>
      )}

      {/* Render HTML bio */}
      <div
        className="prose prose-sm max-w-none text-center mb-4"
        dangerouslySetInnerHTML={{
          __html: selectedMember.bio || "<p>No bio available.</p>",
        }}
      />

      {selectedMember.spouseId && (
        <p className="text-sm mt-2">Spouse ID: {selectedMember.spouseId}</p>
      )}

      {selectedMember.childrenIds.length > 0 && (
        <p className="text-sm mt-1">
          Children IDs: {selectedMember.childrenIds.join(", ")}
        </p>
      )}

      {/* Bottom Close Button */}
      <div className="mt-6 flex justify-center">
        <button
          className="bg-red-500 text-white px-6 py-2 rounded shadow hover:bg-red-600 transition-colors"
          onClick={() => setSelectedMember(null)}
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}

      {/* Controls */}
      <div style={{ position: "absolute", top: 10, left: 10, zIndex: 10 }}>
        <button
          onClick={() => {
            setEditId(null);
            setShowForm(true);
          }}
          className="bg-green-500 text-white px-4 py-2 rounded mb-2"
        >
          ➕ Add Family Member
        </button>
      </div>

      {/* Family Form */}
      {showForm && (
        <div style={{ position: "absolute", top: 60, left: 10, zIndex: 10 }}>
          <FamilyForm
            memberId={editId}
            onSave={() => {
              setShowForm(false);
              setEditId(null);
            }}
          />
        </div>
      )}

      {/* Tree */}
      <div style={{ width: "100%", height: "100%" }}>
        <Tree
          data={familyData}
          orientation="vertical"
          translate={{ x: window.innerWidth / 2, y: 80 }}
          zoomable
          separation={{ siblings: 1.5, nonSiblings: 2 }}
          pathFunc="diagonal"
          renderCustomNodeElement={renderCustomNode}
        />
      </div>
    </div>
  );
}